import { PayRequestStatus, ThreeDSecureStatus, TyroPayRequestResponse } from './@types/pay-requests';
import { ConnectPayClient } from './connect-pay-client';

const POLL_INTERVAL_MS = parseInt(process.env.POLL_INTERVAL_MS);
const PAY_REQUEST_POLL_MAX_RETRIES = parseInt(process.env.PAY_REQUEST_POLL_MAX_RETRIES);
const CHALLENGE_POLL_INTERVAL_MS = parseInt(process.env.CHALLENGE_POLL_INTERVAL_MS);
const CHALLENGE_MAX_RETRIES = parseInt(process.env.CHALLENGE_MAX_RETRIES);
const THREE_D_SECURE_METHOD_MAX_RETRIES = parseInt(process.env.THREE_D_SECURE_METHOD_MAX_RETRIES);

const COMPLETION_STATUSES = [PayRequestStatus.SUCCESS, PayRequestStatus.FAILED];
const INITIAL_UPDATED_STATUSES = [
  PayRequestStatus.SUCCESS,
  PayRequestStatus.FAILED,
  PayRequestStatus.AWAITING_AUTHENTICATION,
];

export class PollingService {
  constructor(private connectPayClient: ConnectPayClient) {}

  public async pollForAwaitingAuthenticationStatus(): Promise<TyroPayRequestResponse | null> {
    const res = await this.pollForResult(
      (payRequestStatus) => INITIAL_UPDATED_STATUSES.includes(payRequestStatus.status),
      POLL_INTERVAL_MS,
      PAY_REQUEST_POLL_MAX_RETRIES,
    );
    if (!res) {
      console.error('pollForInitialStatusUpdate timed out');
      return null;
    }
    if (res.status !== PayRequestStatus.AWAITING_AUTHENTICATION) {
      console.log(res);
      console.error('pollForInitialStatusUpdate unexpected status:', res.status);
      return null;
    }
    return res;
  }

  public async pollFor3DSecureAuthResult(): Promise<TyroPayRequestResponse | null> {
    const res = await this.pollForResult(
      (payRequestStatus) =>
        COMPLETION_STATUSES.includes(payRequestStatus.status) ||
        payRequestStatus.threeDSecure?.status === ThreeDSecureStatus.AWAITING_CHALLENGE,
      POLL_INTERVAL_MS,
      PAY_REQUEST_POLL_MAX_RETRIES,
    );
    return res;
  }

  public async pollFor3DSecureChallengeResult(): Promise<TyroPayRequestResponse | null> {
    const res = await this.pollForResult(
      (payRequestStatus) => COMPLETION_STATUSES.includes(payRequestStatus.status),
      CHALLENGE_POLL_INTERVAL_MS,
      CHALLENGE_MAX_RETRIES,
    );
    return res;
  }

  public async pollFor3DSecureMethodResult(): Promise<TyroPayRequestResponse | null> {
    const updatedPayRequest = await this.pollForResult(
      (payRequestStatus) => payRequestStatus.threeDSecure?.status === ThreeDSecureStatus.AWAITING_AUTH,
      POLL_INTERVAL_MS,
      THREE_D_SECURE_METHOD_MAX_RETRIES,
    );
    return updatedPayRequest;
  }

  private pollForResult = async (
    conditionFn: (statusResult: TyroPayRequestResponse) => boolean,
    pollIntervalMillis: number,
    pollMaxRetries: number,
  ): Promise<TyroPayRequestResponse | null> => {
    let attemptNumber = 0;
    while (attemptNumber < pollMaxRetries) {
      const statusResult = await this.connectPayClient.getPayRequest();
      const conditionResult = conditionFn(statusResult);
      if (conditionResult) {
        return statusResult;
      }
      await new Promise((r) => setTimeout(r, pollIntervalMillis));
      attemptNumber += 1;
    }
    return null;
  };
}
